import moment from "moment";

import { vocabularize } from "src/Common/vocab";

import { DT_OVER_12, DT_RULE_LABELS, DT_SEVENTH_DAY, PAY_RULE_GROUPS, formatHhMm, hasOvertimeRule, isRuleOn, lunchAnchorsByDate, minutesToDecimal, parseHhMm,
  onCallDaysInRange,
  formatClockRange,
} from "./hours";

/**
 * The per-entry breakdown behind one technician's totals — the rows the
 * drill-down shows, shaped once for both the CSV and the printed sheet.
 *
 * Built here rather than in each consumer so the file someone prints and the
 * file they open in Excel can never disagree about a number. They are read side
 * by side during payroll reconciliation, which is exactly when a discrepancy
 * between two views of the same hours costs the most to explain.
 *
 * RT and OT are the derived split where the day could be placed on a clock, and
 * the entered values otherwise — the same figures, chosen the same way, as the
 * drill-down on screen. Travel is always as entered; it is never derived.
 */
export const breakdownRows = (technician) => {
  const anchors = lunchAnchorsByDate(technician.rows);

  return technician.rows.map((row, index) => ({
    technician: technician.technician,
    customer: row.customer || "",
    branch: row.branch || "",
    woNumber: row.woNumber || "",
    date: row.date || "",
    regularTime: row.derivedRegularTime ?? row.regularTime ?? null,
    overTime: row.derivedOverTime ?? row.overTime ?? null,
    // Double time has no entered counterpart: it is only ever derived, so a row
    // the engine could not place carries none rather than falling back to a
    // figure that never represented a premium.
    doubleTime: row.derivedDoubleTime ?? null,
    travelTime: row.derivedTravelTime ?? row.travelTime ?? null,
    // The clock spans behind the figures, for the printed sheet: the same
    // "6:00 AM – 11:00 AM" the screen shows under each figure. Not CSV
    // columns; the file lists its columns explicitly.
    regularSpan: formatClockRange(row.regularStart, row.regularStop),
    travelSpan: formatClockRange(row.travelStart, row.travelStop),
    lunchSpan: formatClockRange(row.lunchStart, row.lunchStop),
    // A lunch belongs to the day, not to any one work order in it, and the
    // endpoint repeats it on every row of that day. Carried only on the row
    // whose span it interrupted — the same row the screen shows it against — so
    // that a column of lunches can be added up without counting one deduction
    // several times over.
    lunchTime: anchors.get(row.date) === index && row.lunchMinutes > 0 ? formatHhMm(row.lunchMinutes) : null,
    // Kept apart from lunchTime, which is a duration meant to be summed. This
    // is a sentence, and only on the day's anchor row for the same reason.
    lunchNote:
      anchors.get(row.date) === index && row.noLunch && !(row.lunchMinutes > 0)
        ? `None taken${row.noLunchReason ? ` — ${row.noLunchReason}` : ""}`
        : anchors.get(row.date) === index && isMissingRequiredLunch(row)
          ? "Required — not recorded"
          : null,
  }));
};

/** Distinct branches a technician logged to, in the order first seen. */
export const branchesFor = (technician) => [
  ...new Set(technician.rows.map((row) => row.branch).filter(Boolean)),
];

/**
 * The caveats that apply to one technician's figures, as plain sentences.
 *
 * These are the same three conditions the screen flags, restated for a document
 * that will be read away from the page that could explain them. A printed sheet
 * filed in a cabinet has no tooltip to click, so a number that needs a caveat
 * has to carry it.
 */
/**
 * A worked day at a branch that requires lunch to be recorded, with neither a
 * lunch nor a "no lunch taken" on it. The rule rides on every row of the day.
 */
export const isMissingRequiredLunch = (row) =>
  row.lunchRequired === true && !(row.lunchMinutes > 0) && !row.lunchStart && !row.noLunch;

/** Distinct dates in a technician's rows that are missing a required lunch. */
export const missingLunchDates = (technician) => {
  const dates = new Set();
  for (const row of technician.rows || []) {
    if (isMissingRequiredLunch(row) && row.date) dates.add(row.date);
  }
  return [...dates].sort();
};

export const caveatsFor = (technician) => {
  const notes = [];

  const missingLunch = missingLunchDates(technician).length;
  if (missingLunch > 0) {
    notes.push(
      `${missingLunch} worked ${missingLunch === 1 ? "day has" : "days have"} no lunch recorded and no "no lunch taken" note, and this technician's branch requires one.`
    );
  }

  if (!hasOvertimeRule(technician)) {
    notes.push(
      "No overtime policy is assigned to this technician, so overtime could not be calculated. The hours below are as entered on the timesheets."
    );
  }
  if (technician.preservedDays > 0) {
    const days = technician.preservedDays;
    notes.push(
      `${days} ${days === 1 ? "day" : "days"} had overtime already entered and ${
        days === 1 ? "was" : "were"
      } taken exactly as recorded rather than recalculated.`
    );
  }
  // No partial-week note on the documents. The screen flags a clipped week
  // where the range is chosen, and whoever prints or exports from Time
  // Management picked that range knowingly — restating it on every sheet was
  // read as a warning about the figures rather than about the range.

  const unattributed = technician.rows.filter((row) => row.derivedOverTime == null).length;
  if (technician.derived && unattributed > 0) {
    notes.push(
      `${unattributed} of these ${vocabularize("work orders")} ${
        unattributed === 1 ? "has" : "have"
      } no clock times, so overtime could not be attributed to a particular one and those hours are shown as entered. The total below is the day or week total either way.`
    );
  }

  return notes;
};

// A cell is quoted whenever it could otherwise change the shape of the row.
// Doubling an embedded quote is what the CSV convention expects; a customer
// name containing a comma is common enough that skipping this corrupts real
// files rather than hypothetical ones.
const csvCell = (value) => {
  const text = value == null ? "" : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const csvRow = (cells) => cells.map(csvCell).join(",");

// Both forms of every bucket: "08:00" is what payroll reads and checks against
// the paper, and 8.00 is what QuickBooks and ADP ingest. Carrying one and
// converting by hand is the manual step this whole item exists to remove.
const hourPair = (value) => {
  const minutes = parseHhMm(value);
  return value == null || value === "" ? ["", ""] : [value, minutesToDecimal(minutes)];
};

const CSV_HEADER = [
  "Technician",
  "Customer",
  "Branch",
  vocabularize("WO #"),
  "Date",
  "Regular Time",
  "Regular (hrs)",
  "Travel Time",
  "Travel (hrs)",
  "Over Time",
  "Over Time (hrs)",
  "Double Time",
  "Double Time (hrs)",
  "Lunch",
  "Lunch Note",
];

/**
 * One technician's breakdown as CSV text.
 *
 * The totals come from the technician entry rather than from adding up the rows
 * above them. Under the weekly policy the threshold is measured across the whole
 * week and under the daily policy across the whole day, so a row's share of the
 * overtime is an attribution rather than an addend — summing the column can
 * legitimately land somewhere else, and the entry's own total is the figure
 * payroll is accountable for.
 */
export const technicianCsv = (technician, { rangeLabel, onCall, range } = {}) => {
  const lines = [csvRow(CSV_HEADER)];

  for (const row of breakdownRows(technician)) {
    lines.push(
      csvRow([
        row.technician,
        row.customer,
        row.branch,
        row.woNumber,
        row.date,
        ...hourPair(row.regularTime),
        ...hourPair(row.travelTime),
        ...hourPair(row.overTime),
        ...hourPair(row.doubleTime),
        row.lunchTime || "",
        row.lunchNote || "",
      ])
    );
  }

  lines.push(
    csvRow([
      `${technician.technician} — TOTAL`,
      "",
      "",
      "",
      "",
      formatHhMm(technician.regularMinutes),
      minutesToDecimal(technician.regularMinutes),
      formatHhMm(technician.travelMinutes),
      minutesToDecimal(technician.travelMinutes),
      formatHhMm(technician.overMinutes),
      minutesToDecimal(technician.overMinutes),
      formatHhMm(technician.doubleMinutes ?? 0),
      minutesToDecimal(technician.doubleMinutes ?? 0),
      "",
      // Lunch Note: a sentence, never totalled.
      "",
    ])
  );

  // Footnotes below a blank line. This file is read and filed rather than piped
  // into anything, and the alternative — two columns that are empty on every
  // row but the last — reads worse for no gain.
  lines.push("");
  // Hours per pay rate, which is how payroll keys them. Travel is paid at the
  // regular rate, so it is folded into that line rather than listed beside it.
  const regularRate = technician.regularMinutes + technician.travelMinutes;
  lines.push(
    csvRow([
      "Regular rate hours (worked + travel)",
      formatHhMm(regularRate),
      minutesToDecimal(regularRate),
      `${formatHhMm(technician.regularMinutes)} worked + ${formatHhMm(technician.travelMinutes)} travel`,
    ])
  );
  lines.push(
    csvRow(["Overtime hours (1.5x)", formatHhMm(technician.overMinutes), minutesToDecimal(technician.overMinutes)])
  );
  lines.push(
    csvRow([
      "Double time hours (2x)",
      formatHhMm(technician.doubleMinutes ?? 0),
      minutesToDecimal(technician.doubleMinutes ?? 0),
    ])
  );
  lines.push(
    csvRow(["Total hours (after lunch)", formatHhMm(technician.totalMinutes), minutesToDecimal(technician.totalMinutes)])
  );
  if (rangeLabel) lines.push(csvRow(["Date range", rangeLabel]));
  for (const option of PAY_RULE_GROUPS[0].options) {
    if (isRuleOn(technician, option)) lines.push(csvRow(["Overtime policy", option.label]));
  }
  for (const key of [DT_OVER_12, DT_SEVENTH_DAY]) {
    if (technician[key] === true) lines.push(csvRow(["Double-time policy", DT_RULE_LABELS[key]]));
  }
  for (const note of caveatsFor(technician)) lines.push(csvRow([note]));

  // On call, after the hours and apart from them: a fact about each day, not
  // a figure, and never added to anything above. Listed so payroll has it on
  // the same file.
  const onCallDays = onCallDaysInRange(onCall, range);
  if (onCallDays.length) {
    lines.push("");
    lines.push(csvRow(["On call (a day, not hours; not counted above)", "Date"]));
    for (const date of onCallDays) lines.push(csvRow(["On call", date]));
    lines.push(csvRow(["On call days", String(onCallDays.length)]));
  }
  return lines.join("\n");
};

// Punctuation out, spaces to hyphens — a filename that survives being saved on
// any of the three operating systems in use here and still reads as a name.
export const slugify = (value) =>
  String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "technician";

export const downloadCsv = (filename, text) => {
  // The BOM is what makes Excel read the file as UTF-8. Without it an accented
  // name arrives mojibaked, and the first person to see it is the one filing
  // the printout.
  const blob = new Blob([`﻿${text}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

/** `timecard-chee-marcus-2026-08-24-to-2026-08-30.csv` */
export const csvFilename = (technician, range) => {
  const suffix =
    range && range[0] && range[1]
      ? `${moment(range[0]).format("YYYY-MM-DD")}-to-${moment(range[1]).format("YYYY-MM-DD")}`
      : moment().format("YYYY-MM-DD");
  return `timecard-${slugify(technician.technician)}-${suffix}.csv`;
};
